import type { Position2D } from '@Components/BigBrowse/Model/ModelTypes';
import type { MapGrid } from '@Model/MapModelsView';
import { pipe } from 'ramda';

type Line = {
  startCell: Position2D;
  endCell: Position2D;
  id: string;
  departmentId: number;
};

type Rectangle = {
  topLeftCell: Position2D;
  width: number;
  height: number;
  departmentId: number;
};

/** Calculates the widest rectangle area for each department in the grid.**/
export const calculateLabelAreas = pipe(
  transposeArrayCoordinates<number | undefined>,
  getLines,
  getRectangles,
  getWidestRectanglePerDepartment
);

/** Inverts a 2D array from [x]y] to [y][x] indexing. **/
function transposeArrayCoordinates<T>(
  grid: (T | undefined)[][]
): (T | undefined)[][] {
  if (!Array.isArray(grid)) {
    throw new TypeError('Input should be a 2D array.');
  }

  // Get maximum length of inner arrays to ensure transposed array includes all elements
  const transposed: (T | undefined)[][] = Array(
    grid.reduce((max, row) => Math.max(max, row.length), 0)
  )
    .fill(undefined)
    .map(() => Array(grid.length).fill(undefined));

  grid.forEach((row, x) => {
    row.forEach((value, y) => {
      transposed[y][x] = value;
    });
  });

  return transposed;
}

/** Extracts the lines from the grid, that contain the same departmentId. **/
function getLines(grid: MapGrid): Map<string, Line> {
  const lines = new Map<string, Line>();
  let lineId = 0;

  const addLineToMap = (
    startCell: Position2D,
    endCell: Position2D,
    departmentId: number
  ) => {
    lines.set(`line_${lineId}`, {
      startCell,
      endCell,
      id: `line_${lineId}`,
      departmentId,
    });
    lineId++;
  };

  for (let y = 0; y < grid.length; y++) {
    // reset on start of each row
    let startPos: Position2D | undefined = undefined;
    let currentVal: number | undefined = undefined;

    for (let x = 0; x < grid[y].length; x++) {
      const cell = grid[y][x];

      if (cell === undefined || cell !== currentVal) {
        // if cell is undefined or cell is not equal to currentVal a line ends
        // add it to map
        if (startPos !== undefined && currentVal !== undefined) {
          addLineToMap(startPos, { x: x - 1, y }, currentVal);
        }
        startPos = cell !== undefined ? { x, y } : undefined;
        currentVal = cell;
      }
    }

    // handle line ending at the end of the row
    if (startPos !== undefined && currentVal !== undefined) {
      addLineToMap(startPos, { x: grid[y].length - 1, y }, currentVal);
    }
  }

  return lines;
}

/** Combines the individual lines into rectangles,
 * by grouping together lines of the same width and horizontal position, and
 * in adjacent rows.
 * **/
function getRectangles(lines: Map<string, Line>): Rectangle[] {
  const rectangles: Rectangle[] = [];

  // Convert the lines map to an array and sort it
  const sortedLines: Line[] = Array.from(lines.values()).sort((a, b) => {
    // Sort by startCell.x, endCell.x, and startCell.y
    if (a.startCell.x !== b.startCell.x) {
      return a.startCell.x - b.startCell.x;
    }
    if (a.endCell.x !== b.endCell.x) {
      return a.endCell.x - b.endCell.x;
    }
    return a.startCell.y - b.startCell.y;
  });

  let currentRectangle: Rectangle | undefined;

  sortedLines.forEach((line) => {
    if (
      // If there is no currentRectangle, or if the line does not extend the currentRectangle
      !currentRectangle ||
      line.startCell.x !== currentRectangle.topLeftCell.x ||
      line.endCell.x !==
        currentRectangle.topLeftCell.x + currentRectangle.width - 1 ||
      line.startCell.y !==
        currentRectangle.topLeftCell.y + currentRectangle.height ||
      line.departmentId !== currentRectangle.departmentId
    ) {
      // Add the currentRectangle to the array of rectangles and start a new rectangle
      if (currentRectangle) {
        rectangles.push(currentRectangle);
      }
      currentRectangle = {
        topLeftCell: line.startCell,
        width: line.endCell.x - line.startCell.x + 1,
        height: 1,
        departmentId: line.departmentId,
      };
    } else {
      // Extend the currentRectangle
      currentRectangle.height++;
    }
  });

  // Add the last rectangle to the array
  if (currentRectangle) {
    rectangles.push(currentRectangle);
  }

  return rectangles;
}

/** Returns the rectangle with the widest width for each department. **/
function getWidestRectanglePerDepartment(rectangles: Rectangle[]): Rectangle[] {
  const groups = new Map<number, Rectangle>();

  rectangles.forEach((rectangle) => {
    const group = groups.get(rectangle.departmentId);
    if (!group || group.width < rectangle.width) {
      groups.set(rectangle.departmentId, rectangle);
    }
  });

  return Array.from(groups.values());
}
